import type {
  CreateParams,
  DataProvider,
  GetListParams,
  GetOneParams,
  UpdateParams,
} from 'ra-core';
import {
  listerLesStructures,
  creerUneStructure,
  lireUneStructure,
  renommerUneStructure,
  listerLesComptesDuneStructure,
  lireLaFicheDunCompte,
  modifierUnCompte,
  listerLesCoursDuneStructure,
  lireLesReglagesDuneStructure,
} from '@groupe-trousseau/shared/api-client';
import { deballer } from './client';
import {
  clefDeListe,
  curseurPour,
  informationDePage,
  oublier,
  retenirLaTranche,
  totalSiConnu,
} from './curseurs';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * **UN SEUL FOURNISSEUR DE DONNÉES, SUR LE CLIENT GÉNÉRÉ — ET RIEN D'AUTRE.**
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * `DOCTRINE.md` §7 : « **un seul data provider REST écrit une fois**, branché sur l'API
 * NestJS existante. Les ressources se déclarent ensuite quasi automatiquement. » Et
 * `admin/CLAUDE.md` ferme le chemin d'à côté : « **il consomme le client généré de
 * `shared/api-client/`, pas des URL écrites à la main** ».
 *
 * **Ce fichier ne porte donc aucune URL**, et c'est vérifiable d'un coup d'œil : il n'y a
 * que des imports de fonctions. §4 : « inventer un endpoint : la fonction n'existe pas, la
 * compilation échoue ».
 *
 * ───────────────────────────────────────────────────────────────────────────
 * **CE QUI N'EST PAS ICI, ET LA LISTE EST LA MOITIÉ DU TRAVAIL.**
 * ───────────────────────────────────────────────────────────────────────────
 *
 * Un fournisseur de données générique implémente neuf méthodes sur toutes les ressources.
 * **Celui-ci refuse, nommément, tout ce que `CDC-5` §6.14 refuse** — et il refuse en levant,
 * pas en rendant une liste vide :
 *
 * - **`delete` sur une structure** : F-56, « **IL N'EXISTE AUCUN GESTE DE SUPPRESSION D'UNE
 *   STRUCTURE** (Q543). Le produit n'a **qu'un seul geste destructif, l'anonymisation d'un
 *   compte**, et il ne s'en ajoute pas un second. » F-68 le redit en critère : « LE SYSTÈME
 *   DOIT n'exposer **aucune route ni aucun geste** de suppression, d'extinction ou de
 *   réactivation d'une structure. »
 * - **`delete` sur un compte** : il s'anonymise, geste nommé, irréversible et tracé.
 * - **`delete` sur un cours** : il se **masque**, et lui seul.
 * - **`updateMany` et `deleteMany`** : F-58 « **aucune saisie en masse** : une structure à
 *   la fois », et F-68 « **aucun envoi en masse n'existe, parce qu'aucun geste de masse
 *   n'existe** ». Une action groupée de la peau qui trouverait ces méthodes en ferait un
 *   geste de masse en trois clics.
 *
 * **Le sens du défaut est délibéré** : lever plutôt que taire. La matrice cache déjà ces
 * boutons ; si l'un reparaît un jour par une pièce de peau non prévue, on veut une erreur
 * lisible, pas une suppression silencieuse. **Deux verrous, et le second est ici.**
 *
 * ───────────────────────────────────────────────────────────────────────────
 * **LES COLONNES SONT CHOISIES — MAIS PAS ICI.**
 * ───────────────────────────────────────────────────────────────────────────
 *
 * `admin/CLAUDE.md` : « aucune colonne affichée par défaut **parce qu'elle existe dans le
 * modèle** : les colonnes sont choisies. Une ressource d'administration **n'est pas une
 * fenêtre sur la base**. »
 *
 * Ce fichier ne choisit rien et ne retire rien : **c'est le contrat qui a déjà choisi**.
 * Les DTO de réponse de `api/src/admin/dto/` n'exposent que les champs décidés, et §6.14
 * porte pour chaque ressource sa colonne « champs interdits en réponse ». Filtrer une
 * seconde fois ici donnerait **deux listes de champs à tenir d'accord**, ce qui est
 * exactement la faute que Q566 nomme sur les chemins : « celle qu'on oublie est la faille ».
 * Les écrans choisissent ce qu'ils **montrent** de ce que le contrat rend.
 */

/** Ce que le message d'un refus dit, pour qu'il ne se lise pas comme une panne. */
function refus(message: string): never {
  throw new Error(message);
}

/** La taille d'une tranche, bornée par le contrat lui-même à cent. */
const TRANCHE_PAR_DEFAUT = 20;

function tranche(perPage: number | undefined): number {
  if (perPage === undefined) return TRANCHE_PAR_DEFAUT;
  return Math.min(Math.max(perPage, 1), 100);
}

/**
 * **La structure courante arrive par le filtre, jamais d'un magasin.**
 *
 * F-55 : « **le compte qui voit tout travaille toujours DANS une structure courante**, sauf
 * sur ses deux surfaces propres. **Sans structure courante, chaque écran porterait deux
 * formes, une filtrée et une non** — et c'est exactement ce qui fait qu'une matrice a une
 * ligne par ressource au lieu de deux. »
 *
 * Deux listes de cette surface sont donc **portées par une structure** : ses comptes et ses
 * cours. Le fournisseur l'exige, et ne va pas la chercher : un fournisseur de données qui
 * lirait un état d'interface aurait deux sources pour un périmètre, et le jour où elles
 * divergent c'est **la liste des comptes d'une autre structure** qui s'affiche.
 *
 * F-55 critère 2 : « QUAND le compte qui voit tout ouvre un écran de structure **sans
 * structure courante**, LE SYSTÈME DOIT **exiger la sélection d'une structure** au lieu de
 * rendre une liste du parc. » Ce refus est cette exigence.
 */
function structureExigee(filtre: Record<string, unknown> | undefined, ressource: string): string {
  const valeur = filtre?.structureId;
  if (typeof valeur !== 'string' || valeur === '') {
    refus(
      `Aucune structure courante n’est choisie : la liste des ${ressource} ne peut pas ` +
        `être rendue sans elle. Choisissez une structure dans le sélecteur en tête de la surface.`,
    );
  }
  return valeur;
}

function texteOuRien(valeur: unknown): string | undefined {
  return typeof valeur === 'string' && valeur !== '' ? valeur : undefined;
}

const provider = {
  // ═════════════════════════════════════════════════════════════════════════
  // LECTURE DE LISTE
  // ═════════════════════════════════════════════════════════════════════════
  getList: async (ressource: string, params: GetListParams) => {
    const page = params.pagination?.page ?? 1;
    const combien = tranche(params.pagination?.perPage);
    const filtre = (params.filter ?? {}) as Record<string, unknown>;
    const clef = clefDeListe(ressource, { ...filtre, combien });

    const position = curseurPour(clef, page);
    if (position === null) {
      refus(
        `Cette liste se parcourt par curseur, une tranche à la fois : la page ${page} ne ` +
          `peut pas être demandée sans avoir vu les précédentes. ` +
          `DOCTRINE.md §5 : « pagination par curseur, jamais par numéro de page ».`,
      );
    }
    const suiteDemandee = position.curseur;

    switch (ressource) {
      /**
       * **La liste des structures — l'écran d'arrivée du compte qui voit tout.**
       *
       * F-55 : « l'écran d'arrivée est **asymétrique par rôle** : la liste des structures
       * pour le compte qui voit tout, l'espace de sa structure pour la personne qui
       * l'administre. » La matrice ferme donc `list` à l'`orgadmin`, qui n'a rien à y voir
       * — il n'aurait qu'une ligne, et F-55 le dit du sélecteur pour la même raison.
       */
      case 'structures': {
        const reponse = await deballer(
          listerLesStructures({
            query: {
              recherche: texteOuRien(filtre.recherche),
              suite: suiteDemandee,
              combien,
            },
          }),
        );
        retenirLaTranche(clef, page, reponse.suite);
        return {
          data: [...reponse.structures],
          pageInfo: informationDePage(page, reponse.suite),
          total: totalSiConnu(page, combien, reponse.structures.length, reponse.suite),
        };
      }

      /**
       * **La liste des comptes rattachés — F-59 : « la liste des comptes rattachés EST
       * l'écran ».**
       *
       * L'ordre est **par date de rattachement, du plus récent au plus ancien, ordre unique
       * et non réglable**, et F-59 en donne le motif, qui n'est pas cosmétique : « **c'est
       * cet ordre qui remplace l'alerte email supprimée en Q114** : on voit les comptes qui
       * viennent d'être créés **en ouvrant la liste**, pas en recevant un email ».
       *
       * **`params.sort` est donc ignoré, et c'est une règle et non une limite.** « Aucun tri
       * réglable » est écrit deux fois, F-59 et F-62. Le contrat ne porte d'ailleurs aucun
       * paramètre de tri sur ces routes : le rendre réglable demanderait de changer le
       * contrat, pas le front.
       */
      case 'comptes': {
        const structureId = structureExigee(filtre, 'comptes');
        const reponse = await deballer(
          listerLesComptesDuneStructure({
            path: { id: structureId },
            query: {
              recherche: texteOuRien(filtre.recherche),
              suite: suiteDemandee,
              combien,
            },
          }),
        );
        retenirLaTranche(clef, page, reponse.suite);
        return {
          data: [...reponse.comptes],
          pageInfo: informationDePage(page, reponse.suite),
          total: totalSiConnu(page, combien, reponse.comptes.length, reponse.suite),
        };
      }

      /**
       * **La liste des cours, et la somme des minutes qui SUIT LE FILTRE.**
       *
       * F-62 : « **UN FILTRE DE DATES EXISTE, ET LA SOMME DES MINUTES SUIT LE FILTRE.** Sans
       * lui, la somme est un total de toujours et **aucun mois ne se facture**. C'est la
       * seule surface du produit où un filtre de dates décide quelque chose. »
       *
       * **La somme voyage donc dans `meta`, avec la tranche**, et non dans un second appel :
       * le serveur la calcule sur la période filtrée, et deux appels pourraient rendre une
       * somme et des lignes qui ne se correspondent pas. `GetListResult` de `ra-core` porte
       * `meta` pour cela, et `useListContext().meta` la rend à l'écran.
       */
      case 'cours': {
        const structureId = structureExigee(filtre, 'cours');
        const reponse = await deballer(
          listerLesCoursDuneStructure({
            path: { id: structureId },
            query: {
              recherche: texteOuRien(filtre.recherche),
              du: texteOuRien(filtre.du),
              au: texteOuRien(filtre.au),
              suite: suiteDemandee,
              combien,
            },
          }),
        );
        retenirLaTranche(clef, page, reponse.suite);
        return {
          data: [...reponse.cours],
          pageInfo: informationDePage(page, reponse.suite),
          total: totalSiConnu(page, combien, reponse.cours.length, reponse.suite),
          meta: { sommeDesMinutes: reponse.sommeDesMinutes },
        };
      }

      default:
        return refus(
          `Aucune liste n’existe pour « ${ressource} » : les trois listes de cette ` +
            `surface sont les structures, les comptes d’une structure et ses cours.`,
        );
    }
  },

  // ═════════════════════════════════════════════════════════════════════════
  // LECTURE D'UN OBJET
  // ═════════════════════════════════════════════════════════════════════════
  getOne: async (ressource: string, params: GetOneParams) => {
    switch (ressource) {
      /**
       * **La fiche d'une structure, et sa forme dépend du rôle.**
       *
       * §11 : « **la forme de réponse dépend du rôle, et c'est la matrice qui l'exige.** Une
       * ressource lue par un `orgadmin` et par le compte qui voit tout **rend deux objets
       * différents**, pas un objet unique avec des conditions à l'intérieur. »
       *
       * Le contrat l'encode par un `oneOf` discriminé sur `role`, et c'est **le serveur qui
       * décide de la branche**. L'écran lit `role` de la réponse : il n'a donc pas à savoir
       * qui il est pour savoir ce qu'il a reçu — un test de rôle côté front pour deviner la
       * forme d'une réponse serait la troisième copie de la même règle.
       *
       * **Son ouverture laisse une ligne de trace**, F-56, et c'est le serveur qui l'écrit.
       */
      case 'structures':
        return {
          data: await deballer(lireUneStructure({ path: { id: String(params.id) } })),
        };

      /**
       * **La fiche d'un compte — F-59 : « elle porte ce que la ligne ne porte pas ».**
       *
       * Les autres rattachements, les relations extérieures, les adresses passées. Et son
       * ouverture **laisse une ligne de trace**, ce qui la distingue du parcours de la liste
       * : F-55, « entrer dans la surface et parcourir une liste ne laissent aucune ligne de
       * trace. **Seule l'ouverture d'une fiche en laisse une.** »
       *
       * **Conséquence à connaître avant d'appeler ceci depuis un écran** : ouvrir la fiche
       * écrit. Un composant qui préchargerait des fiches « pour aller plus vite » écrirait
       * des lignes de trace que personne n'a demandées.
       */
      case 'comptes':
        return {
          data: await deballer(lireLaFicheDunCompte({ path: { id: String(params.id) } })),
        };

      /**
       * **Les cinq réglages d'une structure, plus le réglage du parc.**
       *
       * F-65 : « **UN SEUL ÉCRAN PORTE LES CINQ RÉGLAGES.** Les quatre écrans de réglage
       * rendus par quatre domaines, plus celui du plafond d'élèves, **sont le même écran**. »
       * La réponse porte aussi le bloc du parc pour le `superadmin` — DD-61, « un bloc
       * réglage du parc **sur ce même écran** […] **aucun écran nouveau n'est créé** ».
       *
       * L'identifiant est celui de la structure : la ressource `reglages` n'a pas d'existence
       * propre, ce qui est la conséquence du refus d'un objet « réglage » nom-valeur (§4).
       */
      case 'reglages':
        return {
          data: await deballer(
            lireLesReglagesDuneStructure({ path: { id: String(params.id) } }),
          ),
        };

      default:
        return refus(`Aucune fiche n’existe pour « ${ressource} » sur cette surface.`);
    }
  },

  // ═════════════════════════════════════════════════════════════════════════
  // CRÉATION
  // ═════════════════════════════════════════════════════════════════════════
  create: async (ressource: string, params: CreateParams) => {
    switch (ressource) {
      /**
       * **Créer une structure émet son secret d'appel, et c'est la SEULE fois qu'on le voit.**
       *
       * F-57 : « il est émis par le geste qui crée la structure, **affiché une seule fois et
       * jamais réaffiché**, transmis **hors du produit** ». La structure n'en porte que
       * l'**empreinte** (Q537), « ce qui rend le réaffichage impossible **par
       * construction**, là où la règle ne reposait que sur la discipline ».
       *
       * **La réponse porte donc une valeur qui n'existera plus jamais.** L'écran doit la
       * montrer à ce moment-là et dire qu'elle ne reviendra pas ; la perdre, c'est devoir
       * régénérer — ce qui coupe les appels en cours de la structure, sans période de
       * recouvrement et sans email (F-57, cas de bord 5).
       *
       * `creerUneStructure` rend `{ structureId, secret }` et non un objet de ressource :
       * l'identifiant est donc recomposé pour `ra-core`, et **le secret voyage dans la
       * réponse jusqu'à l'écran, sans passer par aucun cache de liste** — `oublier` jette la
       * mémoire des curseurs, pas le corps.
       */
      case 'structures': {
        const corps = params.data as { nomInterne?: unknown };
        if (typeof corps.nomInterne !== 'string' || corps.nomInterne === '') {
          refus('Une structure se crée avec son nom interne, et il ne peut pas être vide.');
        }
        const reponse = await deballer(
          creerUneStructure({ body: { nomInterne: corps.nomInterne } }),
        );
        oublier('structures');
        return { data: { id: reponse.structureId, secret: reponse.secret } };
      }

      default:
        return refus(
          `Rien ne se crée sous « ${ressource} » par ce chemin. Les rattachements, les ` +
            `relations, les comptes et les cours ont chacun leur geste nommé, et cinq ` +
            `d’entre eux passent par des routes de CDC-1 et CDC-2 — D-070 et D-071.`,
        );
    }
  },

  // ═════════════════════════════════════════════════════════════════════════
  // MODIFICATION
  // ═════════════════════════════════════════════════════════════════════════
  update: async (ressource: string, params: UpdateParams) => {
    switch (ressource) {
      /**
       * **Le nom interne, et lui seul.**
       *
       * F-56 : « le compte qui voit tout modifie le nom interne. **La structure ne le
       * modifie pas.** » Et c'est le seul champ que cette route touche : les colonnes
       * d'affichage ont la leur, les réglages la leur, et **le secret ne se modifie pas** —
       * il se régénère ou se révoque.
       */
      case 'structures': {
        const corps = params.data as { nomInterne?: unknown };
        if (typeof corps.nomInterne !== 'string' || corps.nomInterne === '') {
          refus('Le nom interne d’une structure ne peut pas être vide.');
        }
        const data = await deballer(
          renommerUneStructure({
            path: { id: String(params.id) },
            body: { nomInterne: corps.nomInterne },
          }),
        );
        oublier('structures');
        return { data };
      }

      /**
       * **Un compte : nom, prénom, rôle. JAMAIS l'adresse.**
       *
       * §6.14, colonne des champs interdits : « **L'ADRESSE EMAIL EST INTERDITE EN
       * ÉCRITURE** », borne acquise au domaine des accès et **non élargie ici**. Le corps du
       * contrat ne porte pas le champ, et **un corps qui le porterait reçoit `400`** plutôt
       * que de le voir ignoré — `whitelist` et `forbidNonWhitelisted` côté serveur.
       *
       * **Ce fichier ne le filtre donc pas**, et c'est volontaire : filtrer ici ferait
       * disparaître l'erreur, et un champ silencieusement retiré est pire qu'un refus. Le
       * type du client généré refuse déjà de le porter à la compilation.
       */
      case 'comptes': {
        const corps = params.data as { nom?: unknown; prenom?: unknown; role?: unknown };
        const data = await deballer(
          modifierUnCompte({
            path: { id: String(params.id) },
            body: {
              nom: texteOuRien(corps.nom),
              prenom: texteOuRien(corps.prenom),
              role: corps.role as 'TUTOR' | 'STUDENT' | 'ORGADMIN' | undefined,
            },
          }),
        );
        oublier('comptes');
        return { data };
      }

      default:
        return refus(`Rien ne se modifie sous « ${ressource} » par ce chemin.`);
    }
  },

  // ═════════════════════════════════════════════════════════════════════════
  // CE QUI N'EXISTE PAS, ET QUI LÈVE PLUTÔT QUE DE SE TAIRE
  // ═════════════════════════════════════════════════════════════════════════

  /**
   * **Aucun geste de suppression n'existe sur cette surface, sur aucune ressource.**
   *
   * F-56 (Q543) pour la structure : « le produit n'a **qu'un seul geste destructif,
   * l'anonymisation d'un compte**, et il ne s'en ajoute pas un second. » Pour un compte, ce
   * geste est `anonymiser` ; pour un cours, `masquer` ; pour un rattachement et une
   * relation, `retirer` et `défaire`, qui **laissent la ligne en place avec sa date de fin**.
   *
   * Les quatre sont des **actions métier nommées**, `DOCTRINE.md` §7 : « un admin ne fait pas
   * que du CRUD […] une action métier custom est **du React ordinaire** ». Les faire passer
   * par `delete` les rendrait indistinguables d'une suppression, y compris dans les libellés
   * de la peau — « supprimer » là où le produit dit « anonymiser, irréversible ».
   */
  delete: (ressource: string) =>
    refus(
      `Aucun geste de suppression n’existe sur « ${ressource} ». CDC-5 F-56 : le produit ` +
        `n’a qu’un seul geste destructif, l’anonymisation d’un compte, et il ne s’en ` +
        `ajoute pas un second. Un cours se masque, un rattachement se retire en gardant sa ` +
        `date de fin, une relation se défait.`,
    ),

  /**
   * **Aucun geste de masse n'existe, et deux règles le disent.**
   *
   * F-58 : « **aucune saisie en masse** : une structure à la fois. » F-68 : « **aucun envoi
   * en masse n'existe, parce qu'aucun geste de masse n'existe.** L'email de perte d'accès
   * part **à chaque personne qui perd son dernier rattachement, un par geste**. »
   *
   * Et §12 en écrit le coût, pour qu'un ajout le contredise visiblement : « **éteindre une
   * structure de trois cents personnes demande trois cents gestes** […] **Clause de
   * réouverture : le geste unique est le premier candidat d'une V2.** » Une action groupée
   * qui trouverait ces deux méthodes livrerait ce geste unique **sans que la clause soit
   * ouverte**.
   */
  updateMany: (ressource: string) =>
    refus(
      `Aucun geste de masse n’existe sur « ${ressource} ». CDC-5 F-58 : « aucune saisie ` +
        `en masse, une structure à la fois », et F-68 : « aucun envoi en masse n’existe, ` +
        `parce qu’aucun geste de masse n’existe ». Le geste unique est nommé comme premier ` +
        `candidat d’une V2 — §12, clause de réouverture.`,
    ),

  deleteMany: (ressource: string) =>
    refus(
      `Aucun geste de masse n’existe sur « ${ressource} », et aucun geste de suppression ` +
        `non plus : les deux interdits se rencontrent ici.`,
    ),

  /**
   * **`getMany` et `getManyReference` n'ont aucune route, et n'en réclament pas.**
   *
   * `ra-core` s'en sert pour résoudre des références — « montre-moi le nom de la structure
   * de cette ligne ». **Cette surface n'en a pas besoin** : le contrat résout les références
   * dans la réponse elle-même. La liste des cours porte `participants[].structureId` **et**
   * la fiche d'un compte porte `rattachements[].structureNom` — F-62 : « **la structure de
   * chaque participant est ce que l'écran d'historique d'une personne ne porte pas**, et une
   * structure en a besoin sur un cours mixte ».
   *
   * C'est la conséquence de « une ressource d'administration n'est pas une fenêtre sur la
   * base » : les réponses sont composées pour les écrans, pas normalisées pour un ORM.
   */
  getMany: (ressource: string) =>
    refus(
      `« ${ressource} » ne se lit pas par lot : le contrat résout ses références dans la ` +
        `réponse — la structure d’un participant, le nom d’une structure sur un ` +
        `rattachement — au lieu de laisser l’écran les recoller.`,
    ),

  getManyReference: (ressource: string) =>
    refus(
      `Aucune liste liée n’existe pour « ${ressource} » : les comptes et les cours d’une ` +
        `structure se lisent par leur propre route, portée par la structure.`,
    ),
};

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * **LE SEUL TRANSTYPAGE DE CE FICHIER, ET IL EST INHÉRENT À `ra-core`.**
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Les méthodes de `DataProvider` sont **génériques en `RecordType`** : `getList` promet de
 * rendre `RecordType[]` pour **n'importe quel** `RecordType` que l'appelant choisira. Aucune
 * implémentation qui rend une forme concrète — la nôtre, celle du contrat — ne peut satisfaire
 * cette promesse, et c'est correct du point de vue du compilateur : `RecordType` « pourrait
 * être instancié avec un sous-type différent ».
 *
 * Les fournisseurs officiels y échappent sans le vouloir : ils déballent du JSON en `any`, donc
 * `RecordType` se résout à `any` et la variance ne se pose pas. **Ce serait le contraire d'un
 * gain ici** — nos méthodes ci-dessus sont typées par le contrat, champ par champ, et le
 * compilateur refuse déjà un champ inventé au point d'appel du client généré.
 *
 * **Le transtypage est donc à la frontière, une fois, et l'intérieur reste strict.** Ce qui est
 * abandonné est la promesse générique de `ra-core`, que personne ne tient ; ce qui est gardé est
 * le typage réel des vingt-deux réponses. L'inverse — un `any` par méthode — aurait rendu
 * silencieux exactement ce que `DOCTRINE.md` §4 existe pour faire échouer à la compilation.
 */
export const fournisseurDeDonnees = provider as DataProvider;
